import {defineStore} from "pinia";
import {reactive} from "vue";
import TokenService from "@/services/token.service";
import api from "@/services/api/memberApi";
import {useRouter} from "vue-router";
import {useMyPageStore} from "@/services/store/myPageStore";
import commonCode from "@/services/api/commonCode";
import {togglePop} from "@/utils/commonUi";
import reflashAndBackControl from "@/utils/reflashAndBackControl";
import {useComponent} from "@/utils/compossable/$component";
import {deleteIndexedDB} from "@/plugins/IndexedDB";

const INIT_DATA = () => {
    return {
        actorId: "",
        birthday: "",
        email: "",
        emailMasked: "",
        joinChnlCd: "",
        joinDttm: "",
        joinStcd: "",
        mbrCls: "",
        mbrNm: "",
        mbrNmMasked: "",
        mbrNo: "",
        mpno: "",
        nationaltyCd: "",
        sex: "",            // 성별
        occpCd: "",        // 직업코드
        occpNm: "",        // 직업명
        rnAddr: '',           // 주소
        detailAddr: '',     // 상세주소
        detailAddrMasked: "",
        oauthType: '',      // 최종 소셜로그인 종류
        status: {
            isLogin: false,
            isConnect: false
        },
        loginMethodType: '',
        noticeTo: "",
        noticeCheck: false,
        certCheck: false,
        claimComplete: false,
        lastLoginMethod: "",    // sns마지막으로 로그인한 타입
        lastLogoutDttm: "",
        zip: "",
        canGoBack: true,
        deviceUuid: "",
        tokenTimeCheck: false
    }
}

export const useMemberStore = defineStore(
    "useMemberStore",
    () => {

        const member = reactive({status: {isLogin: false}});
        const router = useRouter();

        /**
         * 초기화
         */
        const init = () => {
            Object.assign(member, INIT_DATA());
        }

        const setItem = (value) => {
            for (const key in value) {
                member[key] = value[key];
            }
        }

        const resetClaimNoticeCheck = () => {
            member.noticeCheck = false;
            if (member.loginMethodType === 'pwd' || member.loginMethodType === 'sns') {
                member.certCheck = false;
            } else {
                if (member.claimComplete) {
                    //청구 완료일시 인증여부 초기화
                    member.certCheck = false;
                }
            }
        }

        const errMessage = (err) => {
            const $component = useComponent();
            if (err.response.data.failureMessage.exceptionCode === 'USER_LOCKED') {
                $component.simpleAlert('아이디 혹은 비밀번호를 잘못 입력하셨어요.\n비밀번호 변경으로 이동합니다.\n확인 후 다시 시도해주세요.');
                $component.alert.fn.fnSelect = function () {
                    togglePop('certificatePop', true);
                }
            } else if (err.response.data.failureMessage.exceptionCode === 'AUTHENTICATION_CERT_NOT_EXIST') {
                $component.simpleAlert('해당 인증서로 연동된 계정이 없어요. 로그인 후\n[환경설정 > 인증/보안]에서 추가 가능해요.');
            } else if (err.response.data.failureMessage.exceptionCode === 'USER_NOT_EXIST') {
                $component.simpleAlert(err.response.data.failureMessage.exceptionSuggest);
                $component.alert.fn.fnSelect = function () {
                    router.push('/claim/web/termsAgree');
                }
            } else {
                $component.simpleAlert(err.response.data.failureMessage.exceptionMessage)
            }
        }

        /**
         * 로그인 후 회원데이터 셋팅
         * @param data
         */
        const bind = (data) => {
            localStorage.setItem('loginId', data.loginIdPwd.loginId);
            Object.assign(member, data.member);
            TokenService.setUser(data);
            member.status.isLogin = true;
            member.loginMethodType = data.loginMethodType ? data.loginMethodType : "pwd";
            member.lastLogoutDttm = data.latestLoginHistory ? data.latestLoginHistory.logoutDttm : '';
            if (member.loginMethodType === 'pwd' || member.loginMethodType === 'sns') {
                member.certCheck = false;
            } else {
                member.certCheck = true;
            }
        }

        /**
         * 라온시큐어 키보드보안 로그인
         * @param params
         * @returns {Promise<null extends ShallowRef<infer V> ? V : (null extends Ref<infer V> ? UnwrapRefSimple<V> : UnwrapRefSimple<null>)>}
         */
        const nxKeyDecode = async (params) => {
            await api.nxKeyDecode(params)
                .then(async (res) => {
                    await bind(res.result);
                    await loginSuccess();
                })
        }

        /**
         * 공인인증서 로그인
         * @param params
         * @returns {Promise<void>}
         */
        const officialLogin = async (params) => {
            if (params.status === 1) {
                await api.ksbizLoginAction(params)
                    .then(async (res) => {
                        await bind(res.result);
                        await loginSuccess();
                    })
                    .catch(err => {
                        errMessage(err);
                    })
            }else if (params.status === 0){
                document.getElementById('official').focus();
            }
        }

        /**
         * 금융인증서 로그인
         * @param params
         * @param context
         * @returns {Promise<void>}
         */
        const financeLogin = async (params, context) => {
            if (params.status === 1) {
                await api.ksbizFinanceLoginAction(params, context)
                    .then(async (res) => {
                        await bind(res.result);
                        await loginSuccess();
                    })
                    .catch(err => {
                        errMessage(err);
                    })
            }else if (params.status === 0){
                document.getElementById('financial').focus();
            }
        }

        /**
         * 간편인증 로그인
         * @param params
         * @returns {Promise<void>}
         */
        const simpleLogin = async (params) => {
            const parseData = JSON.parse(params);
            let obj = {
                txId: parseData.txId,
                token: parseData.token
            }
            await api.getSimpleLogin(obj)
                .then(async (res) => {
                    await bind(res.result);
                    await loginSuccess();
                })
                .catch(err => {
                    errMessage(err);
                })
        }

        /**
         * 로그인 성공 callBack
         * @returns {Promise<void>}
         */
        const loginSuccess = async () => {
            localStorage.removeItem('loginFailCount');
            await useMyPageStore().fnLoginMethod();
            //3개월마다 비밀번호 변경 팝업창 오픈
            await commonCode.getPasswordChangeSuggest()
                .then(async (res) => {
                    const pwdChangeDt = new Date(res.pwdChangeDttm); //변경했던 날짜
                    const pwdChacngeEndDt = new Date(pwdChangeDt.setDate(pwdChangeDt.getDate() + res.pwdChangeDttmDeferredDayCnt)); //변경해야하는 날짜
                    const today = new Date(); //오늘날짜
                    if (pwdChacngeEndDt.toDateString() === today.toDateString()) {
                        reflashAndBackControl.handlerSetReflashAndBack();
                        togglePop('passwordNoticePop', true);
                    } else {
                        //로그인 후 이동할 페이지가 있을 경우 해당 페이지로 이동
                        if (sessionStorage.getItem('accessPath') && member.status.isLogin) {
                            await router.push(sessionStorage.getItem('accessPath'));
                            sessionStorage.removeItem('accessPath');
                        }
                        //로그인 후 이동할 페이지가 없을 경우 메인으로 이동
                        else if (member.status.isLogin) {
                            await router.push({name: 'Main'});
                        }
                    }
                })
        }

        /**
         * 소셜 로그인
         * @param params
         * @returns {Promise<null extends ShallowRef<infer V> ? V : (null extends Ref<infer V> ? UnwrapRefSimple<V> : UnwrapRefSimple<null>)>}
         */
        const oauthLogin = async (params) => {
            const data = await api.getOauthLogin(params);
            if (data?.result ?? false) {
                member.status.isConnect = true;
                sessionStorage.setItem("latestLoginMethod", params.oauthType);
                await bind(data.result);
            } else {
                sessionStorage.setItem("latestLoginMethod", '');
                member.status.isConnect = false
            }
            return data;
        }

        /**
         * 로그아웃
         */
        const logout = () => {
            const $component = useComponent();
            api.getLogout()
                .then(async () => {
                    await $component.delTimer();
                    await sessionStorage.clear();
                    await init();
                    await router.push({name: 'Main'});
                    deleteIndexedDB();
                })
                .catch(err => {
                    $component.simpleAlert('로그아웃중 에러가 발생했습니다.\n잠시 후 다시 시도해 주세요.');
                })
        }

        /**
         * 청구 노티스 모달 (하루동안 보지않기 여부)
         */
        const isExistsNoticeCookie = () => {
            return $cookies.get('hideNoticeModal')
        }

        /**
         * 청구 노티스 모달 (하루동안 보지않기 여부) 셋팅,제거
         */
        const setNoticeCookie = (isRemove = false) => {
            if (!isRemove) {
                $cookies.set('hideNoticeModal', 'true', '1d');
            } else {
                $cookies.remove('hideNoticeModal');
            }
        }

        const setCanGoBack = (canGoBack) => {
            member.canGoBack = canGoBack;
        }

        return {
            member,
            init,
            bind,
            oauthLogin,
            logout,
            nxKeyDecode,
            isExistsNoticeCookie,
            setNoticeCookie,
            loginSuccess,
            setItem,
            officialLogin,
            financeLogin,
            simpleLogin,
            resetClaimNoticeCheck,
            setCanGoBack
        }
    },
    {
        persist: {
            storage: sessionStorage
        }
    }
)